import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection } from "@/components/FaqSection";
import { SITE_URL, hreflangLinks, faqPageSchema } from "@/lib/seo";

const PATH = "/gold-vs-silver-pakistan";
const TITLE = "Gold vs Silver Pakistan — Live Sona Aur Chandi Ka Rate + Gold-Silver Ratio";
const DESC = "Gold vs silver rate in Pakistan today — live sona aur chandi ka bhao per tola, gold-silver ratio, investment comparison. 24K gold vs pure silver, updated every 10 minutes.";

const FAQS = [
  { q: "Aaj Pakistan mein sona aur chandi ka rate kya hai?", a: "Sona (gold) 24K aur chandi (silver) dono ka live rate hamare gold rate in Pakistan aur silver rate in Pakistan pages par har 10 minute update hota hai. Sona per tola PKR mein aur chandi bhi per tola aur per kg mein available hai." },
  { q: "Gold aur silver mein kaunsa investment better hai Pakistan mein?", a: "Gold zyada stable, long-term wealth store hai — 5% average annual return over 20 years. Silver zyada volatile hai (±20% moves), industrial demand se linked, potentially higher returns but higher risk. Diversified portfolio mein dono 80/20 split typically recommend hota hai." },
  { q: "Gold-silver ratio kya hai?", a: "Gold-silver ratio = gold price ÷ silver price. Historically 50–80 ka range. Agar ratio 90+ ho, silver undervalued mana jata hai. Agar 40 se neeche, gold undervalued. Aaj ka live ratio hamare hub page par update hota hai." },
  { q: "Chandi ka bhao Pakistan mein kaise calculate hota hai?", a: "Chandi ka retail rate = international XAG/USD spot × USD/PKR rate + local sarafa premium. Same formula as gold, sirf metal alag." },
  { q: "Sona aur chandi mein kaunsa faster appreciate hota hai?", a: "Bull markets mein silver typically faster rise karta hai (2020, 2011 examples), lekin corrections bhi zyada tez hote hain. Gold slow-and-steady, silver high-beta play." },
  { q: "Pakistan mein sona aur chandi kahan se khareedein?", a: "Karachi (Saddar Bolton Market), Lahore (Anarkali sarafa), Islamabad (Blue Area), Rawalpindi (Raja Bazaar) ke registered sarafa dealers se. Hallmark ya BSP certification zaroor check karein." },
];

export const Route = createFileRoute("/gold-vs-silver-pakistan")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "en_PK" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }, ...hreflangLinks(PATH)],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqPageSchema(FAQS)) }],
  }),
  component: GoldVsSilverPage,
});

function GoldVsSilverPage() {
  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/pakistan" className="hover:text-primary">Pakistan Gold Rate</Link> / vs Silver
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold">Gold vs Silver Pakistan — Sona Aur Chandi Ka Rate Comparison</h1>
      <p className="mt-4 text-foreground/85 leading-relaxed">
        Aaj Pakistan mein <strong>sona aur chandi</strong> ke rates, gold-silver
        ratio aur investment comparison — sab ek jagah. Both metals move together
        broadly but silver has 2–3× higher volatility. Ye page decide karne
        mein help karega ke aap ke portfolio ke liye kaunsa metal better hai.
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <Link to="/pakistan" className="rounded-2xl border border-border bg-card p-6 hover:border-primary">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Sona (Gold)</div>
          <div className="text-lg font-bold mt-1">Live 24K gold rate in Pakistan</div>
          <div className="text-sm text-muted-foreground mt-2">Per tola, per 10 gram, per gram. Refreshed every 10 minutes.</div>
        </Link>
        <Link to="/silver-rate-pakistan" className="rounded-2xl border border-border bg-card p-6 hover:border-primary">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Chandi (Silver)</div>
          <div className="text-lg font-bold mt-1">Live chandi rate in Pakistan</div>
          <div className="text-sm text-muted-foreground mt-2">Per tola, per kg, per gram. Refreshed every 10 minutes.</div>
        </Link>
      </div>

      <section className="mt-8 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-xl font-bold mb-3">Gold vs Silver — quick comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr><th className="text-left p-3">Feature</th><th className="text-left p-3">Gold (Sona)</th><th className="text-left p-3">Silver (Chandi)</th></tr>
            </thead>
            <tbody>
              {[
                ["Purity standard", "24K, 22K, 21K, 18K", "999 pure silver"],
                ["Typical PKR price (per tola)", "~PKR 300,000+", "~PKR 3,500+"],
                ["Volatility", "Moderate (±10% / year)", "High (±20% / year)"],
                ["Industrial demand", "~10%", "~50% (electronics, solar)"],
                ["Best for", "Long-term wealth", "Higher-risk / higher-reward"],
                ["Zakat nisab (Pakistan)", "7.5 tola", "52.5 tola"],
              ].map((r) => (
                <tr key={r[0]} className="border-t border-border">
                  {r.map((c, i) => <td key={i} className="p-3">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <Link to="/pakistan/zakat-nisab" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold text-sm">Zakat nisab calculator</div>
          <div className="text-xs text-muted-foreground mt-1">Gold + silver nisab combined.</div>
        </Link>
        <Link to="/pakistan/gold-forecast" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold text-sm">Gold price forecast Pakistan</div>
          <div className="text-xs text-muted-foreground mt-1">Will gold rate decrease?</div>
        </Link>
        <Link to="/pakistan/market-hours" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold text-sm">Sarafa bazaar timings</div>
          <div className="text-xs text-muted-foreground mt-1">Why is gold market closed today?</div>
        </Link>
      </div>

      <FaqSection faqs={FAQS} title="Gold vs Silver Pakistan — FAQs" />
    </main>
  );
}